import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonsContainer } from 'record-it-components/button/ButtonsContainer/ButtonsContainer';
import { ButtonStandard, ButtonStandardType } from 'record-it-components/button/ButtonStandard/ButtonStandard';
import { DateTimeInput } from 'record-it-components/dateTime/DateTimeInput/DateTimeInput';
import { ExpandableInlineCreationForm, ExpandableInlineCreationFormProps } from 'record-it-components/form/ExpandableInlineCreationForm/ExpandableInlineCreationForm';
import { FormGroup } from 'record-it-components/form/FormGroup/FormGroup';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { useOpened } from 'record-it-components/hooks/useOpened/useOpened';
import { MessageBox, MessageBoxType } from 'record-it-components/misc/MessageBox/MessageBox';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { useRequiredWorkTimeEntryToCreate } from '../../../hooks/createWorkTimeEntry/useRequiredWorkTimeEntryToCreate';
import { validateBreak } from '../../../hooks/validation/functions/validateBreak';
import { useValidation } from '../../../hooks/validation/useValidation';
import styles from '../../page/CreateWorkTimeEntryPage/CreateWorkTimeEntryPage.module.css';
export type CreateTimeEntryBreakInlineProps = PropsWithoutChildren<Omit<ExpandableInlineCreationFormProps, 'children' | 'label' | 'opened' | 'buttons' | 'notOpenedContent' | 'openedContent'>> & {
  /**
   * Start date a new break is initially set to.
   */
  initialStartDate?: Date;
  /**
   * End date a new break is initially set to.
   */
  initialEndDate?: Date;
  onCreate: (timeEntryBreak: TimeEntryBreakToCreate) => void;
};
export type TimeEntryBreakToCreate = {
  startDate: string;
  endDate: string;
};
export const CreateTimeEntryBreakInline: FunctionComponent<CreateTimeEntryBreakInlineProps> = React.memo(props => {
  const $ = _c(75);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props,
      ignoreChildren: true
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let initialEndDate;
  let initialStartDate;
  let onCreate;
  let remainingProps;
  if ($[2] !== t0) {
    ({
      onCreate,
      initialStartDate,
      initialEndDate,
      ...remainingProps
    } = t0);
    $[2] = t0;
    $[3] = initialEndDate;
    $[4] = initialStartDate;
    $[5] = onCreate;
    $[6] = remainingProps;
  } else {
    initialEndDate = $[3];
    initialStartDate = $[4];
    onCreate = $[5];
    remainingProps = $[6];
  }
  let t1;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Date();
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const [startDate, setStartDate] = useState(t1);
  let t2;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Date();
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const [endDate, setEndDate] = useState(t2);
  const {
    opened,
    close,
    open
  } = useOpened();
  const {
    t
  } = useTranslation();
  const workTimeEntry = useRequiredWorkTimeEntryToCreate();
  let t3;
  if ($[9] !== endDate || $[10] !== startDate || $[11] !== workTimeEntry) {
    t3 = validateBreak({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    }, workTimeEntry);
    $[9] = endDate;
    $[10] = startDate;
    $[11] = workTimeEntry;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== endDate || $[14] !== startDate || $[15] !== workTimeEntry) {
    t4 = [startDate, endDate, workTimeEntry];
    $[13] = endDate;
    $[14] = startDate;
    $[15] = workTimeEntry;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const canCreateBreakValidation = useValidation(t3, t4);
  let t5;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = newDate => {
      if (newDate) {
        setStartDate(newDate);
      }
    };
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const handleStartDateInput = useEventCallback(t5);
  let t6;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = newDate_0 => {
      if (newDate_0) {
        setEndDate(newDate_0);
      }
    };
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const handleEndDateInput = useEventCallback(t6);
  let t7;
  if ($[19] !== initialEndDate || $[20] !== initialStartDate || $[21] !== open) {
    t7 = () => {
      const date = new Date();
      date.setSeconds(0);
      date.setMilliseconds(0);
      setStartDate(initialStartDate ?? new Date(date));
      setEndDate(initialEndDate ?? new Date(date));
      open();
    };
    $[19] = initialEndDate;
    $[20] = initialStartDate;
    $[21] = open;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const handleStartCreatingClick = useEventCallback(t7);
  let t8;
  if ($[23] !== close || $[24] !== endDate || $[25] !== onCreate || $[26] !== startDate) {
    t8 = () => {
      onCreate({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });
      close();
    };
    $[23] = close;
    $[24] = endDate;
    $[25] = onCreate;
    $[26] = startDate;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const handleCreateClick = useEventCallback(t8);
  let t9;
  if ($[28] !== t) {
    t9 = t("components.timeTracking.CreateTimeEntryBreakInline.label");
    $[28] = t;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== canCreateBreakValidation.errors || $[31] !== canCreateBreakValidation.valid) {
    t10 = !canCreateBreakValidation.valid ? <MessageBox type={MessageBoxType.Error} className={styles.ErrorMessageBox} data-testid="CreateWorkTimeEntryPage.ErrorMessageBox">{canCreateBreakValidation.errors.map(_temp)}</MessageBox> : undefined;
    $[30] = canCreateBreakValidation.errors;
    $[31] = canCreateBreakValidation.valid;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== t) {
    t11 = t("entities.WorkTimeEntry.WorkTimeEntryBreak.startDate");
    $[33] = t;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== handleStartDateInput || $[36] !== startDate || $[37] !== t11) {
    t12 = <FormGroup><DateTimeInput label={t11} value={startDate} onInput={handleStartDateInput} data-testid="CreateTimeEntryBreakInline.StartDateInput" /></FormGroup>;
    $[35] = handleStartDateInput;
    $[36] = startDate;
    $[37] = t11;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== t) {
    t13 = t("entities.WorkTimeEntry.WorkTimeEntryBreak.endDate");
    $[39] = t;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== endDate || $[42] !== handleEndDateInput || $[43] !== t13) {
    t14 = <FormGroup><DateTimeInput label={t13} value={endDate} onInput={handleEndDateInput} data-testid="CreateTimeEntryBreakInline.EndDateInput" /></FormGroup>;
    $[41] = endDate;
    $[42] = handleEndDateInput;
    $[43] = t13;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== t10 || $[46] !== t12 || $[47] !== t14) {
    t15 = <>{t10}{t12}{t14}</>;
    $[45] = t10;
    $[46] = t12;
    $[47] = t14;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  const t16 = !canCreateBreakValidation.valid;
  let t17;
  if ($[49] !== t) {
    t17 = t("components.timeTracking.CreateTimeEntryBreakInline.create");
    $[49] = t;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] !== handleCreateClick || $[52] !== t16 || $[53] !== t17) {
    t18 = <ButtonStandard type={ButtonStandardType.Primary} onClick={handleCreateClick} disabled={t16} data-testid="CreateTimeEntryBreakInline.CreateButton">{t17}</ButtonStandard>;
    $[51] = handleCreateClick;
    $[52] = t16;
    $[53] = t17;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  let t19;
  if ($[55] !== t) {
    t19 = t("components.timeTracking.CreateTimeEntryBreakInline.cancel");
    $[55] = t;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[57] !== close || $[58] !== t19) {
    t20 = <ButtonStandard type={ButtonStandardType.Primary} onClick={close} data-testid="CreateTimeEntryBreakInline.CancelButton">{t19}</ButtonStandard>;
    $[57] = close;
    $[58] = t19;
    $[59] = t20;
  } else {
    t20 = $[59];
  }
  let t21;
  if ($[60] !== t18 || $[61] !== t20) {
    t21 = <ButtonsContainer>{t18}{t20}</ButtonsContainer>;
    $[60] = t18;
    $[61] = t20;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  let t22;
  if ($[63] !== t) {
    t22 = t("components.timeTracking.CreateTimeEntryBreakInline.startCreatingBreak");
    $[63] = t;
    $[64] = t22;
  } else {
    t22 = $[64];
  }
  let t23;
  if ($[65] !== handleStartCreatingClick || $[66] !== t22) {
    t23 = <ButtonsContainer><ButtonStandard type={ButtonStandardType.Primary} onClick={handleStartCreatingClick} data-testid="CreateTimeEntryBreakInline.StartCreatingButton">{t22}</ButtonStandard></ButtonsContainer>;
    $[65] = handleStartCreatingClick;
    $[66] = t22;
    $[67] = t23;
  } else {
    t23 = $[67];
  }
  let t24;
  if ($[68] !== opened || $[69] !== remainingProps || $[70] !== t15 || $[71] !== t21 || $[72] !== t23 || $[73] !== t9) {
    t24 = <ExpandableInlineCreationForm {...remainingProps} label={t9} opened={opened} openedContent={t15} buttons={t21} notOpenedContent={t23} />;
    $[68] = opened;
    $[69] = remainingProps;
    $[70] = t15;
    $[71] = t21;
    $[72] = t23;
    $[73] = t9;
    $[74] = t24;
  } else {
    t24 = $[74];
  }
  return t24;
});
function _temp(errorText) {
  return <div key={errorText}>{errorText}</div>;
}